<template>
<div>
    <div class="row">
        <div class="col-12 mb-4 d-flex flex-row align-items-center" style="gap: 0.5rem">
            <h1 class="mr-auto">{{ id ? $t('edit-event'): $t('new-event') }}</h1>
            <button
                v-if="id"
                :class="`btn btn-outline-${bulkEdit ? 'primary' : 'secondary'}`"
                @click="bulkEdit = !bulkEdit"
            >
                {{ $t('edit-many') }} <b-icon icon="pencil" class="ml-1" />
            </button>
            <button
                :disabled="loading || submitting"
                type="button"
                class="btn btn-primary"
                @click="showSaveDialog"
            >
                {{ id ? $t('save') : $t('create') }}
            </button>
            <button
                v-if="id"
                :disabled="loading || submitting"
                type="button"
                class="btn btn-outline-danger"
                @click="showDeleteDialog"
            >
                {{ $t('delete') }}
            </button>
        </div>
        <div v-if="saved" class="col-12 mb-2">
            <div class="alert alert-success">{{ $t('event-saved') }}</div>
        </div>
        <div v-else-if="error.errors" class="col-12 mb-2">
            <div class="alert alert-danger">{{ $t('missing-required-fields') }}</div>
        </div>

        <div v-if="!loading" class="col-12 mb-2">
            <div class="list-group border-radius-2 no-border mb-4">
                <div class="list-group-item d-flex flex-row align-items-center flex-wrap" style="gap: 1rem">
                    <DateInput v-show="!bulkEdit" v-model="date" :label="$t('date')" class="flex-grow-1" :min="timetable.datefrom" :max="timetable.dateto" :error="error.errors && error.date" @input="error.date = false" />
                    <TimeInput v-model="from" :label="$t('from')" class="flex-grow-1" :error="error.errors && error.from" @input="error.from = false" />
                    <TimeInput v-model="to" :label="$t('until')"  class="flex-grow-1" :error="error.errors && error.to" @input="error.to = false" />
                </div>
                <div class="list-group-item d-flex flex-row align-items-center" style="gap: 1rem">
                    <div>
                        <SelectInput
                            v-model="event.ttsessiontype"
                            @input="error.ttsessiontype = false"
                            :label="$t('session-type')"
                            :options="sessiontypes"
                            optionLabel="name"
                            style="min-width: 200px"
                            :nullLabel="$t('none')"
                            :error="error.errors && error.ttsessiontype"
                        />
                    </div>
                    <div>
                        <SelectInput
                            v-if="event.ttsessiontype"
                            v-model="event.ttroom"
                            :label="$t('room')"
                            :options="event.ttsessiontype.ttrooms"
                            optionLabel="name"
                            style="min-width: 200px"
                        />
                    </div>
                </div>
                <div class="list-group-item">
                    <TextInput v-model="event.title" :label="$t('title')" />
                </div>
                <div v-show="!bulkEdit" class="list-group-item">
                    <TextAreaInput v-model="event.ttevent_notes" :label="$t('notes')" rows="2" />
                </div>
            </div>

            <div v-if="bulkEdit" class="list-group border-radius-2 no-border mb-2">
                <div class="list-group-item font-weight-bold d-flex align-items-center">
                    <span class="mr-4">{{ $t('select-events-to-update') }}</span>
                    <YesNoToggle v-model="showContiguous" class="mr-auto my-0" :yesLabel="$t('this-week')" :noLabel="$t('weekly')" :noFirst="true" :label="$t('match-weekly-or-this-week')" :srOnly="true" @input="updateIds = []" />
                    <button type="button" class="btn btn-sm btn-outline-primary" @click="updateIds = (updateIds.length ? [] : (showContiguous ? contiguousEvents : similarEvents).map(e => e.id))">{{ $t('toggle-all') }}</button>
                </div>
                <div class="list-group-item d-flex flex-wrap align-items-center" style="gap: 0.5rem">
                    <div
                        v-for="similar in (showContiguous ? contiguousEvents : similarEvents)"
                        :key="similar.id"
                        class="rounded border shadow-sm p-2 d-flex flex-column"
                        :class="{ 'bg-light border-dark': updateIds.includes(similar.id) }"
                        style="gap: 0.25rem; cursor: pointer;"
                        @click="toggleUpdateId(similar.id)"
                    >
                        <span>{{$datetime.timestampToDay(similar.from) }}</span>
                        <span>{{$datetime.getLocaleDateStringFromTimestamp(similar.from) }}</span>
                        <TimetableEvent :event="similar" class="p-1" />
                    </div>
                </div>
            </div>
        </div>


        <template v-if="!bulkEdit">
            <div v-if="clashes.length" class="col-12 mb-2" :class="{'col-md-6': clashes.length && capacity }">
                <div class="list-group border-radius-2 no-border mb-4">
                    <div class="list-group-item list-group-item-danger text-sm font-weight-bold">
                        {{ $t('clashes') }}
                    </div>
                    <div v-for="clash in clashes" :key="`${clash.user_id}-${clash.ttevent_id}`" class="list-group-item list-group-item-danger d-flex align-items-center">
                        <span class="mr-auto">{{ clash.displayname }}</span>
                        <button class="btn btn-sm btn-danger" @click="$router.push(`/timetables/${clash.tttimetable_id}/events/${clash.ttevent_id}`)">
                            {{ $t('view-event') }}<b-icon icon="arrow-right" class="ml-2"></b-icon>
                        </button>
                    </div>
                </div>
            </div>

            <div v-if="capacity && Number(capacity.capacity) && capacity.capacity < capacity.attending" class="col-12 mb-2" :class="{'col-md-6': clashes.length && capacity }">
                <div class="list-group border-radius-2 no-border mb-4">
                    <div class="list-group-item list-group-item-info text-sm font-weight-bold">
                        {{  $t('over-capacity-msg', { 'n': capacity.attending, 'm': capacity.capacity, 'o': capacity.events.length - (id ? 1 : 0) }) }}
                        <span v-if="capacity.events.length - (id ? 1 : 0) > 0">{{ $tc('over-capacity-events', capacity.events.length - (id ? 1 : 0)) }}</span>
                    </div>
                    <div v-for="ev in capacity.events.filter(e => e.id != id)" :key="ev.id" class="list-group-item list-group-item-info d-flex align-items-center">
                        {{ $t('event-with-n-students', { 'n': ev.attending}) }}
                        <button type="button" class="btn btn-sm btn-info ml-auto" @click="$router.push(`/timetables/${ev.tttimetable_id}/events/${ev.id}`)">
                            {{ $t('view-event') }}<b-icon icon="arrow-right" class="ml-2"></b-icon>
                        </button>
                    </div>
                </div>
            </div>

            <div v-if="!loading && !id" class="col-12 mb-4">
                <RecurEventSettings :event="event" />
            </div>
        </template>
    </div>

    <div v-if="!loading && !bulkEdit" class="row flex-lg-row-reverse">
        <div class="col-12 col-lg-6">
            <div class="list-group border-radius-2 no-border mb-4">
                <div class="list-group-item">
                    <EventTeacherEditor v-model="event.teachers" />
                </div>
            </div>
        </div>

        <div class="col-12 col-lg-6">
            <div class="list-group border-radius-2 no-border mb-4">
                <div class="list-group-item">
                    <EventStudentEditor
                        v-model="event.students"
                        :options="timetable.students"
                    />
                </div>
            </div>
        </div>

        <div class="col-12 mb-4 d-flex flex-row align-items-center">
            <button
                :disabled="loading || submitting"
                type="button"
                class="btn btn-primary ml-auto"
                @click="showSaveDialog"
            >
                {{ id ? $t('save') : $t('create') }}
            </button>
        </div>
    </div>
</div>
</template>

<script>
import { dateFromISODate, dateToISODate } from "src/modules/timetable/util"
import SessionManager from "src/modules/session/sessionManager"
import DateInput from "src/components/Inputs/DateInput.vue"
import TimeInput from "src/components/Inputs/TimeInput.vue"
import TextAreaInput from "src/components/Inputs/TextAreaInput.vue"
import SelectInput from "src/components/Inputs/SelectInput.vue"
import EventStudentEditor from "src/components/Events/EventStudentEditor.vue"
import EventTeacherEditor from "src/components/Events/EventTeacherEditor.vue"
import RecurEventSettings from "src/components/Timetables/RecurEventSettings.vue"
import TimetableEvent from "src/components/Timetables/TimetableEvent.vue"
import YesNoToggle from "src/components/Inputs/YesNoToggle.vue"

export default {
    components: {
        DateInput,
        TimeInput,
        TextAreaInput,
        SelectInput,
        EventStudentEditor,
        EventTeacherEditor,
        RecurEventSettings,
        TimetableEvent,
        YesNoToggle,
    },
    mounted() {
        Promise.all([this.fetchTimetable(), this.fetchEvent(), this.fetchSessions()]).then(() => {
            if (this.id) {
                Promise.all([this.fetchSimilar(), this.fetchContiguous()]).then(() => {
                    const state = this.sessionManager.get('state');
                    if(state){
                    	this.bulkEdit = state.bulkEdit
                    	this.showContiguous = state.showContiguous
                    } else {
                    	this.bulkEdit = false;
                    	this.showContiguous = false;
                    }
                    this.sessionManager.set('state', {})

                    this.loading = false
                })
            } else {
                this.loading = false
            }

            this.event.ttsessiontype = this.sessiontypes.filter(el => el.id == this.event.ttsessiontype_id)[0]
            const ttroom_id = this.event.ttroom ? this.event.ttroom.id : undefined;

            if (ttroom_id) {
                this.event.ttroom = this.event.ttsessiontype.ttrooms.find(r => r.id == ttroom_id)
            }

            if (this.event.students && this.event.students.length && this.id) {
                this.fetchClashes()
                this.fetchCapacity()
            }
        })
    },
    data() {
        return {
            loading: true,
            submitting: false,
            timetable: null,
            id: Number(this.$route.params.eventId),
            event: {
                ttsessiontype: null,
                ttroom: null,
                title: '',
                ttevent_notes: '',
                students: [],
            },
            saved: false,
            date: null,
            from: null,
            to: null,
            sessiontypes: [],
            showOptions: false,
            notifyStudents: false,
            error: {
                errors: false,
                date: false,
                from: false,
                to: false,
                ttsessiontype: false,
            },
            createoradd: false,
            clashes: [],
            capacity: null,
            similarEvents: [],
            contiguousEvents: [],
            showContiguous: false,
            bulkEdit: false,
            updateIds: [],

            sessionManager: new SessionManager(this.$route)
        }
    },
    methods: {
        fetchTimetable() {
            return this.$cfetch(`${this.$settings.APIBaseURL}/timetables/get`, {
                method: 'GET',
                data: { id: this.$route.params.timetableId },
            }).then(res => {
                if (res.status !== 'SUCCESS') throw new Error()

                this.timetable = res.result
                this.timetable.students = this.timetable.students.map(student => {
                    return {
                        ...student,
                        id: student.user_id,
                    }
                })
            }).catch(err => {
                console.error(err)
                this.$log.error.call(this, err)
                this.$store.commit('openDialog', {
                    title: this.$t('something-went-wrong'),
                    content: this.$t('error-fetching-timetable'),
                    confirmCallBack: () => {
                        this.$router.push('/')
                    }
                })
            })
        },
        fetchEvent() {
            if (!this.id) return Promise.resolve()

            return this.$cfetch(`${this.$settings.APIBaseURL}/timetables/events/get`, {
                method: 'GET',
                data: { id: this.id },
            }).then(res => {
                if (res.status !== 'SUCCESS') throw new Error()

                this.event = res.result
                this.date = this.event.ttevent_datetimefrom.split(' ')[0]
                this.from = this.event.ttevent_datetimefrom.split(' ')[1]
                this.to = this.event.ttevent_datetimeto.split(' ')[1]
            }).catch(err => {
                this.$log.error.call(this, err)
                this.$store.commit('openDialog', {
                    title: this.$t('something-went-wrong'),
                    content: this.$t('error-fetching-event'),
                    confirmCallBack: () => {
                        this.$router.push('/')
                    }
                })
            })
        },
        fetchSessions() {
            return this.$cfetch(`${this.$settings.APIBaseURL}/timetables/sessiontypes/all`, {
                method: 'GET',
            }).then(res => {
                if (res.status !== 'SUCCESS') throw new Error()

                this.sessiontypes = res.results
            }).catch(err => {
                this.$log.error.call(this, err)
                this.$store.commit('openDialog', {
                    title: this.$t('something-went-wrong'),
                    content: this.$t('error-fetching-session-types'),
                })
            })
        },
        fetchSimilar() {
            return this.$cfetch(`${this.$settings.APIBaseURL}/timetables/events/all`, {
                method: 'POST',
                data: {
                    timetable_id: this.timetable.id,
                    from: this.date,
                    to: this.timetable.dateto,
                    start: this.from,
                    end: this.to,
                    ttsessiontype_id: this.event.ttsessiontype_id,
                    ttroom_id: this.event.ttroom_id,
                    title: this.title,
                    dayofweek: dateFromISODate(this.date).getDay() + 1,
                }
            }).then(res => {
                if (res.status !== 'SUCCESS') throw new Error(res)

                this.similarEvents = res.results.map(ev => {
                    ev.ttsessiontype = this.sessiontypes.find(st => st.id == ev.ttsessiontype_id)
                    ev.ttroom = ev.ttsessiontype.ttrooms.find(r => r.id == ev.ttroom_id)
                    return ev
                })
            }).catch(err => {
                console.error(err)
                this.$log.error.call(this, err)
            })
        },
        fetchContiguous() {
            const date = dateFromISODate(this.date)
            date.setDate(date.getDate() - (date.getDay() + 6) % 7)

            const from = dateToISODate(date)
            date.setDate(date.getDate() + 6)
            const to = dateToISODate(date)

            return this.$cfetch(`${this.$settings.APIBaseURL}/timetables/events/all`, {
                method: 'POST',
                data: {
                    timetable_id: this.timetable.id,
                    from,
                    to,
                    ttsessiontype_id: this.event.ttsessiontype_id,
                    ttroom_id: this.event.ttroom_id,
                    title: this.event.title,
                }
            }).then(res => {
                if (res.status !== 'SUCCESS') throw new Error(res)

                this.contiguousEvents = res.results.map(ev => {
                    ev.ttsessiontype = this.sessiontypes.find(st => st.id == ev.ttsessiontype_id)
                    ev.ttroom = ev.ttsessiontype.ttrooms.find(r => r.id == ev.ttroom_id)
                    return ev
                })
            }).catch(err => {
                console.error(err)
                this.$log.error.call(this, err)
            })
        },
        async saveEvent() {
            if (this.submitting) return Promise.resolve()

            document.getElementById('main').scrollTo({ top: 0 })

            this.error = {
                errors: !this.date || !this.from || !this.to || !this.event.ttsessiontype,
                date: !this.date,
                from: !this.from,
                to: !this.to,
                ttsessiontype: !this.event.ttsessiontype,
            }

            if (this.error.errors) return Promise.resolve()

            if (this.alertIfNoStudents()) return

            this.$store.commit('showBlockingSpinner')

            await Promise.all([this.fetchClashes(), this.fetchCapacity()]).finally(() => {
                this.$store.commit('hideBlockingSpinner')
            })

            let clashRes
            let clashRej
            const clashPromise = new Promise(res => clashRes = res, rej => clashRej = rej)

            let capRes
            let capRej
            const capPromise = new Promise(res => capRes = res, rej => capRej = rej)

            if (this.clashes.length) {
                this.$store.commit('openDialog', {
                    title: this.$t('clashes-found'),
                    content: this.$t('clashes-found-msg'),
                    confirmCallBack: clashRes,
                    cancelCallBack: clashRej,
                    cancelBtn: true,
                    confirmBtnText: this.$t('save-anyway'),
                    cancelBtnText: this.$t('view-clashes'),
                })
            } else {
                clashRes()
            }

            await clashPromise.catch()

            if (this.capacity && Number(this.capacity.capacity)  && this.capacity.capacity < this.capacity.attending) {
                this.$store.commit('openDialog', {
                    title: this.$t('over-capacity'),
                    content: this.$t('over-capacity-msg', { 'n': this.capacity.attending, 'm': this.capacity.capacity, 'o': this.capacity.events.length - (this.id ? 1 : 0) })
                    + ((this.capacity.events.length - (this.id ? 1 : 0) > 0) ? ` ${this.$tc('over-capacity-events', this.capacity.events.length - (this.id ? 1 : 0))}` : ''),
                    confirmCallBack: capRes,
                    cancelCallBack: capRej,
                    cancelBtn: true,
                    confirmBtnText: this.$t('save-anyway'),
                    cancelBtnText: this.$t('back'),
                })
            } else {
                capRes()
            }

            await capPromise.catch()

            this.$store.commit('showBlockingSpinner')
            this.submitting = true
            this.saved = false

            return this.$cfetch(`${this.$settings.APIBaseURL}/timetables/events/save`, {
                method: 'POST',
                data: {
                    createoradd: this.createoradd,
                    id: this.id ? this.id : undefined,
                    tttimetable_id: this.timetable.id,
                    ttsessiontype_id: this.event.ttsessiontype.id,
                    ttroom_id: this.event.ttroom ? this.event.ttroom.id : undefined,
                    title: this.event.title,
                    notes: this.event.ttevent_notes,
                    date: this.date,
                    from: this.from,
                    to: this.to,
                    students: this.event.students,
                    teachers: this.event.teachers,
                    notify: this.notifyStudents,
                    recur_count: this.event.recur_count,
                    recur_period: this.event.recur_period,
                },
            }).then(res => {
                if (res.status == 'SUCCESS'){
                    this.saved = true
                    this.createoradd = false

                    if (!this.id) {
                        this.$router.push(`/timetables/edit/${this.timetable.id}?from=${this.date}`)
                    }
                } else {
                    if(res.errorType && res.errorType == 'EVENT_CONFLICT'){
                        var self = this;
                        this.$store.commit('openDialog', {
                            title: this.$t('found-matching-events'),
                            content: this.$t('matching-events-in-timetable'),
                            cancelBtn: true,
                            cancelBtnText: this.$t("update-existing-events"),
                            confirmBtnText: this.$t("cancel"),
                            confirmCallBack: function() {
                            },
                            cancelCallBack: function() {
                                self.createoradd = true;
                                self.saveEvent();
                            }
                        });
                    } else {
                        this.$store.commit('openDialog', {
                            title: this.$t('something-went-wrong'),
                            content: this.$t('error-saving-event'),
                        });
                    }
                }
            }).catch(err => {
                console.error(err)
                this.$log.error.call(this, err)
                this.$store.commit('openDialog', {
                    title: this.$t('something-went-wrong'),
                    content: this.$t('error-saving-event'),
                })
            }).finally(() => {
                this.$store.commit('hideBlockingSpinner')
                this.createoradd = false
                this.submitting = false
            })
        },
        bulkSave() {
            if (this.submitting) return
            this.submitting = true

            this.$store.commit('showBlockingSpinner')

            return this.$cfetch(`${this.$settings.APIBaseURL}/timetables/events/bulkupdate`, {
                method: 'POST',
                data: {
                    ttevent_ids: this.updateIds,
                    from: this.from,
                    to: this.to,
                    ttsessiontype_id: this.event.ttsessiontype.id,
                    ttroom_id: this.event.ttroom ? this.event.ttroom.id : undefined,
                    title: this.event.title,
                    notify: this.notifyStudents,
                },
            }).then(res => {
                if (res.status !== 'SUCCESS') throw new Error(res)

                this.sessionManager.set('state', {
                    bulkEdit: this.bulkEdit,
                    showContiguous: this.showContiguous,
                })

                location.reload()
            }).catch(err => {
                console.error(err)
                this.$log.error.call(this, err)
            }).finally(() => {
                this.submitting = false
                this.$store.commit('hideBlockingSpinner')
            })
        },
        bulkDelete() {
            if (this.submitting) return
            this.submitting = true

            this.$store.commit('showBlockingSpinner')

            return this.$cfetch(`${this.$settings.APIBaseURL}/timetables/events/bulkdelete`, {
                method: 'POST',
                data: {
                    ttevent_ids: this.updateIds,
                    notify: this.notifyStudents,
                },
            }).then(res => {
                if (res.status !== 'SUCCESS') throw new Error(res)

                this.$router.go(-1)
            }).finally(() => {
                this.$store.commit('hideBlockingSpinner')
                this.submitting = false
            })
        },
        fetchClashes() {
            return this.$cfetch(`${this.$settings.APIBaseURL}/timetables/events/clash`, {
                method: 'POST',
                data: {
                    timetable_id: this.timetable.id ,
                    from: `${this.date} ${this.from}`,
                    to: `${this.date} ${this.to}`,
                    user_ids: this.event.students.map(st => st.id),
                    event_id: this.id ? this.id : undefined,
                },
            }).then(res => {
                if (res.status !== 'SUCCESS') {
                    this.$store.commit('openDialog', {
                        title: this.$t('something-went-wrong'),
                        content: this.$t('error-checking-clashes')
                    })
                    return
                }

                this.clashes = res.results
            }).catch(err => {
                this.$log.error.call(this, err)
                this.$store.commit('openDialog', {
                    title: this.$t('something-went-wrong'),
                    content: this.$t('error-checking-clashes')
                })
            })
        },
        fetchCapacity() {
            if (!this.event.ttroom) return Promise.resolve()

            return this.$cfetch(`${this.$settings.APIBaseURL}/timetables/rooms/capacity`, {
                method: 'GET',
                data: {
                    ttroom_id: this.event.ttroom.id,
                    from: `${this.date} ${this.from}`,
                    to:  `${this.date} ${this.to}`,
                }
            }).then(res => {
                if (res.status !== 'SUCCESS') throw res

                if (!res.result.capacity) {
                    this.capacity = {
                        attending: this.event.students.length,
                        capacity: Number(this.event.ttroom.capacity),
                        events: [],
                    }

                    return
                }

                this.capacity = res.result
                if (!this.id) this.capacity.attending += this.event.students.length

            }).catch(err => {
                this.$log.error.call(this, err)
            })
        },
        async showDeleteDialog() {
            let deleteRes, deleteRej
            const deletePromise = new Promise(res => deleteRes = res, rej => deleteRej = rej)

            this.$store.commit('openDialog', {
                title: this.$t('are-you-sure'),
                content: this.$t('are-you-sure-delete-event'),
                cancelBtn: true,
                confirmCallBack: deleteRes,
                cancelCallBack: deleteRej,
                confirmBtnText: this.$t('delete'),
            })

            await deletePromise.catch()

            let notifyRes
            const notifyPromise = new Promise(res => notifyRes = res)

            this.$store.commit('openDialog', {
                title: this.$t('deleting-event'),
                content: this.$t('notify-students-question'),
                cancelBtn: true,
                confirmCallBack: () => {
                    this.notifyStudents = true
                    notifyRes()
                },
                confirmBtnText: this.$t('notify'),
                cancelBtnText: this.$t('no'),
                cancelCallBack: notifyRes,
            })

            notifyPromise.then(() => {
                (this.bulkEdit ? this.bulkDelete() : this.deleteEvent()).finally(() => {
                    this.notifyStudents = false
                })
            })
        },
        showSaveDialog() {
            if (this.bulkEdit) {
                this.showBulkSaveDialog()
                return
            }

            if (this.alertIfNoStudents()) return

            if (this.timetable.status != 2) {
                this.saveEvent()
                return
            }

            this.$store.commit('openDialog', {
                title: this.$t('saving-event'),
                content: this.$t('notify-students-question'),
                cancelBtn: true,
                confirmCallBack: () => {
                    this.notifyStudents = true
                    this.saveEvent().finally(() => {
                        this.notifyStudents = false
                    })
                },
                confirmBtnText: this.$t('notify'),
                cancelBtnText: this.$t('no'),
                cancelCallBack: this.saveEvent
            })
        },
        async showBulkSaveDialog() {
            let saveRes, saveRej
            const savePromise = new Promise(res => saveRes = res, rej => saveRej = rej)

            this.$store.commit('openDialog', {
                title: this.$t('are-you-sure'),
                content: this.$t('bulk-save-events-msg'),
                confirmCallBack: saveRes,
                cancelCallBack: saveRej,
                cancelBtn: true,
            })

            await savePromise.catch()

            if (this.timetable.status != 2) {
                this.bulkSave()
                return
            }

            this.$store.commit('openDialog', {
                title: this.$t('saving-event'),
                content: this.$t('notify-students-question'),
                cancelBtn: true,
                confirmCallBack: () => {
                    this.notifyStudents = true
                    this.bulkSave().then(() => {
                        this.notifyStudents = false
                    })
                },
                confirmBtnText: this.$t('notify'),
                cancelBtnText: this.$t('no'),
                cancelCallBack: this.bulkSave
            })
        },
        deleteEvent() {
            if (this.submitting) return
            this.submitting = true

            this.$store.commit('showBlockingSpinner')

            this.$cfetch(`${this.$settings.APIBaseURL}/timetables/events/delete`, {
                method: 'DELETE',
                data: { id: this.id },
            }).then(res => {
                if (res.status !== 'SUCCESS') throw new Error()

                this.$router.go(-1)
            }).catch(err => {
                console.error(err)
                this.$log.error.call(this, err)
                this.$store.commit('openDialog', {
                    title: this.$t('something-went-wrong'),
                    content: this.$t('error-deleting-event'),
                })
            }).finally(() => {
                this.submitting = false
                this.$store.commit('hideBlockingSpinner')
            })
        },
        alertIfNoStudents() {
            if (!this.event.students.length) {
                this.$store.commit('openDialog', {
                    title: this.$t('no-students'),
                    content: this.$t('please-add-students-msg')
                })
                return true
            }
            return false
        },
        toggleUpdateId(id) {
            if (this.updateIds.includes(id)) {
                this.updateIds = this.updateIds.filter(el => el !== id)
            } else {
                this.updateIds = [ ...this.updateIds, id ]
            }
        }
    },
}
</script>
